import { Component, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../shared/services/auth';
import { CrmService } from '../../shared/services/crm';
import { RouterLink } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';

@Component({
  selector: 'app-portal',
  standalone: true,
  imports: [FormsModule, RouterLink, CommonModule],
  providers: [DatePipe],
  template: `
    <main class="max-w-7xl mx-auto px-8 py-32">
      @if (!auth.isAuthenticated()) {
        <!-- Login/Register/Forgot Toggle -->
        <div class="max-w-md mx-auto bg-white border border-stone-200 p-10 shadow-sm">
          <header class="text-center mb-10">
            <h1 class="font-serif text-4xl text-on-surface mb-4">
              {{ isForgotPassword() ? 'Reset Password' : (isLogin() ? 'Welcome Back' : 'Create Account') }}
            </h1>
            <p class="text-on-surface-variant">
              {{ isForgotPassword() ? 'Enter your email to receive password reset instructions.' : (isLogin() ? 'Access your sanctuary maintenance dashboard.' : 'Join Safe Haven for a seamless booking experience.') }}
            </p>
          </header>

          @if (isForgotPassword()) {
            <form (submit)="onForgotPassword($event)" class="space-y-6">
              <div>
                <label class="block text-xs font-bold tracking-widest uppercase mb-2">Email Address</label>
                <input type="email" name="email" [(ngModel)]="formData.email" required class="w-full h-12 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
              </div>

              @if (error()) {
                <p class="text-red-600 text-sm text-center font-bold">{{ error() }}</p>
              }
              @if (forgotSuccess()) {
                <p class="text-emerald-700 text-sm text-center font-bold bg-emerald-50 p-4 border border-emerald-200">{{ forgotSuccess() }}</p>
              }

              <button type="submit" class="w-full h-14 bg-on-surface text-white font-bold tracking-widest uppercase hover:bg-stone-800 transition-colors disabled:opacity-50" [disabled]="isLoading()">
                {{ isLoading() ? 'Sending...' : 'Send Reset Instructions' }}
              </button>

              <div class="text-center mt-6">
                <button type="button" (click)="toggleForgotPassword(false)" class="text-sm text-stone-500 font-bold hover:text-on-surface">
                  ← Back to Login
                </button>
              </div>
            </form>
          } @else {
            <form (submit)="onSubmit($event)" class="space-y-6">
              @if (!isLogin()) {
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-bold tracking-widest uppercase mb-2">First Name</label>
                    <input type="text" name="firstName" [(ngModel)]="formData.firstName" class="w-full h-12 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                  </div>
                  <div>
                    <label class="block text-xs font-bold tracking-widest uppercase mb-2">Last Name</label>
                    <input type="text" name="lastName" [(ngModel)]="formData.lastName" class="w-full h-12 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                  </div>
                </div>
              }
              <div>
                <label class="block text-xs font-bold tracking-widest uppercase mb-2">Email Address</label>
                <input type="email" name="email" [(ngModel)]="formData.email" class="w-full h-12 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
              </div>
              <div>
                <div class="flex justify-between items-center mb-2">
                  <label class="block text-xs font-bold tracking-widest uppercase">Password</label>
                  @if (isLogin()) {
                    <button type="button" (click)="toggleForgotPassword(true)" class="text-xs text-gold-accent font-bold hover:underline">
                      Forgot password?
                    </button>
                  }
                </div>
                <input type="password" name="password" [(ngModel)]="formData.password" class="w-full h-12 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
              </div>

              @if (error()) {
                <p class="text-red-600 text-sm text-center font-bold">{{ error() }}</p>
              }

              <button type="submit" class="w-full h-14 bg-on-surface text-white font-bold tracking-widest uppercase hover:bg-stone-800 transition-colors disabled:opacity-50" [disabled]="isLoading()">
                {{ isLoading() ? 'Processing...' : (isLogin() ? 'Login' : 'Create Account') }}
              </button>
            </form>

            <div class="mt-8 text-center">
              <button (click)="isLogin.set(!isLogin()); error.set(null)" class="text-sm text-gold-accent font-bold hover:underline">
                {{ isLogin() ? "Don't have an account? Register" : 'Already have an account? Login' }}
              </button>
            </div>
          }
        </div>
      } @else {
        <!-- Portal Content -->
        <header class="mb-16 flex justify-between items-end border-b border-stone-200 pb-8">
          <div>
            <h1 class="font-serif text-5xl text-on-surface mb-4">My Sanctuary</h1>
            <p class="text-xl text-on-surface-variant">Welcome back, {{ auth.currentUser()?.name }}.</p>
          </div>
          <button (click)="auth.logout()" class="text-sm font-bold tracking-widest uppercase text-stone-400 hover:text-red-600 transition-colors">Logout</button>
        </header>

        <section>
          <div class="flex justify-between items-center mb-8">
            <h2 class="font-serif text-3xl">Scheduled Services</h2>
            <button routerLink="/book" class="text-gold-accent font-bold flex items-center gap-2 hover:underline">
              <span class="material-symbols-outlined">add</span>
              Book New Service
            </button>
          </div>
          
          @if (isLoading()) {
            <div class="animate-pulse space-y-4">
              <div class="h-24 bg-stone-100 border border-stone-200"></div>
              <div class="h-24 bg-stone-100 border border-stone-200"></div>
            </div>
          } @else if (appointments().length === 0) {
            <div class="bg-stone-50 border-2 border-dashed border-stone-200 p-20 text-center">
              <span class="material-symbols-outlined text-stone-300 text-6xl mb-4">calendar_today</span>
              <p class="text-on-surface-variant mb-8">No scheduled cleanings found.</p>
              <button routerLink="/book" class="inline-flex items-center gap-2 bg-gold-accent text-black font-bold h-14 px-12 rounded-lg shadow-lg hover:brightness-105 active:scale-95 transition-all">
                Book Your First Service <span class="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>
          } @else {
            <div class="grid grid-cols-1 gap-6">
              @for (appt of appointments(); track appt.id) {
                <div class="bg-white border border-stone-200 p-8 flex items-center justify-between hover:shadow-md transition-shadow">
                  <div class="flex items-center gap-8">
                    <div class="w-16 h-16 bg-stone-50 border border-stone-100 flex flex-col items-center justify-center rounded-lg">
                      <span class="text-xs font-bold text-stone-400 uppercase">{{ appt.startTime | date:'MMM' }}</span>
                      <span class="text-2xl font-bold text-on-surface">{{ appt.startTime | date:'dd' }}</span>
                    </div>
                    <div>
                      <h3 class="font-serif text-2xl mb-1">{{ appt.name }}</h3>
                      <p class="text-on-surface-variant font-bold text-sm tracking-widest uppercase flex items-center gap-2">
                        <span class="material-symbols-outlined text-gold-accent text-base">person</span>
                        {{ appt.specialistName }} • {{ appt.startTime | date:'h:mm a' }}
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-6">
                    <span class="px-4 py-2 bg-stone-50 text-xs font-bold tracking-widest uppercase text-on-surface-variant border border-stone-200 rounded-full">
                      {{ appt.status }}
                    </span>
                    <button class="material-symbols-outlined text-stone-300 hover:text-gold-accent transition-colors">more_vert</button>
                  </div>
                </div>
              }
            </div>
          }
        </section>
      }
    </main>
  `,
  styles: [`
    :host { display: block; }
  `],
})
export class PortalComponent implements OnInit {
  auth = inject(AuthService);
  crm = inject(CrmService);

  isLogin = signal(true);
  isForgotPassword = signal(false);
  isLoading = signal(false);
  error = signal<string | null>(null);
  forgotSuccess = signal<string | null>(null);
  appointments = signal<any[]>([]);

  formData = {
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  };

  ngOnInit() {
    if (this.auth.isAuthenticated()) {
      this.fetchAppointments();
    }
  }

  toggleForgotPassword(show = true) {
    this.isForgotPassword.set(show);
    this.error.set(null);
    this.forgotSuccess.set(null);
  }

  onForgotPassword(e: Event) {
    e.preventDefault();
    if (!this.formData.email) {
      this.error.set('Please enter your email address');
      return;
    }
    this.isLoading.set(true);
    this.error.set(null);
    this.forgotSuccess.set(null);

    this.auth.forgotPassword(this.formData.email).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.forgotSuccess.set(res.message || 'Reset instructions sent to your email.');
      },
      error: (err) => {
        this.isLoading.set(false);
        this.error.set(err.error?.error || 'Failed to process request');
      }
    });
  }

  onSubmit(e: Event) {
    e.preventDefault();
    this.isLoading.set(true);
    this.error.set(null);

    const obs = this.isLogin() 
      ? this.auth.login({ email: this.formData.email, password: this.formData.password })
      : this.auth.register(this.formData);

    obs.subscribe({
      next: () => {
        this.isLoading.set(false);
        this.fetchAppointments();
      },
      error: (err) => {
        this.error.set(err.error?.error || 'Authentication failed');
        this.isLoading.set(false);
      }
    });
  }

  fetchAppointments() {
    this.isLoading.set(true);
    this.crm.getMyAppointments().subscribe({
      next: (data) => {
        this.appointments.set(data);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }
}
